import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { getFirestore, collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { User } from 'firebase/auth';
import { MainCommit } from '@finvari/deliver';

interface MainCommitsContextType {
  mainCommits: MainCommit[];
  loading: boolean;
  error: string | null;
  lastRefreshed: number | null;
}

const MainCommitsContext = createContext<MainCommitsContextType | undefined>(undefined);

export const useMainCommits = (): MainCommitsContextType => {
  const context = useContext(MainCommitsContext);
  if (!context) {
    throw new Error('useMainCommits must be used within a MainCommitsProvider');
  }
  return context;
};

interface MainCommitsProviderProps {
  children: ReactNode;
  user: User | null;
}

export const MainCommitsProvider: React.FC<MainCommitsProviderProps> = ({ children, user }) => {
  const [mainCommits, setMainCommits] = useState<MainCommit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<number | null>(null);

  useEffect(() => {
    // Only set up Firestore listener if user is authenticated
    if (!user) {
      setLoading(false);
      setMainCommits([]);
      setError(null);
      setLastRefreshed(null);
      return;
    }

    setLoading(true);
    setError(null);

    const db = getFirestore();
    const mainCommitsRef = collection(db, 'mainCommits');

    // Query main commits ordered by their order field (0 is most recent)
    const q = query(mainCommitsRef, orderBy('order', 'asc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const commits: MainCommit[] = [];
      let latestRefreshed: number | null = null;

      snapshot.docs.forEach(doc => {
        const data = doc.data() as MainCommit;
        commits.push(data);
        if (data.lastRefreshed && (!latestRefreshed || data.lastRefreshed > latestRefreshed)) {
          latestRefreshed = data.lastRefreshed;
        }
      });

      setMainCommits(commits);
      setLastRefreshed(latestRefreshed);
      setLoading(false);
    }, (err) => {
      setError(err.message || 'Failed to load main commits');
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]); // Now depends on user state

  const value: MainCommitsContextType = {
    mainCommits,
    loading,
    error,
    lastRefreshed,
  };

  return (
    <MainCommitsContext.Provider value={value}>
      {children}
    </MainCommitsContext.Provider>
  );
};
